import { useAppContext } from "@context/app";
import Button from "@elements/button";
import { CartItemModel, CartStep } from "@types";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import CartItem from "./cartItem";
import PathIcon from "@svg/PathIcon";
import Modal from "@modules/layout/modal";
import WarningIcon from "@svg/WarningIcon";
import { updateCart, useIsMount } from "@utils/helpers";
import WrappingDonationBanner from '@modules/donation/WrappingDonationBanner';
import Image from "next/image";
import { WrappingProblemType } from "@templates/cartContainer";

interface GiftProps {
  changeStep: (value: CartStep) => void;
  action: (value: {
    next?: CartStep;
    current: CartStep;
    goCheckout?: boolean;
  }) => void;
  wrappingProblems: { id: string | number; type: WrappingProblemType }[];
  removeProblems: () => void;
  hasFeatureAnnouncement: boolean;
}

const Gift: React.FC<GiftProps> = ({
  changeStep,
  action,
  wrappingProblems,
  removeProblems,
  hasFeatureAnnouncement,
}) => {
  const { setUserCart, cart, syncData } = useAppContext();
  const t = useTranslations();
  const { locale } = useRouter();
  const [cartItems, setCartItems] = useState<CartItemModel[]>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const ItemsQty = cartItems.reduce((previousValue, currentValue) => {
    return (
      previousValue +
      (currentValue.gifts.find(
        (g) =>
          !!g.card?.id ||
          g.wrapping ||
          (Array.isArray((g as any).balloons) && (g as any).balloons.some((b: any) => (Number(b?.count) || 0) > 0))
      ) || currentValue.customText?.length

        ? 1
        : 0)
    );
  }, 0);
  const [goToCheckout, setGoCheckout] = useState<boolean>(false);

  useEffect(() => {
    setUserCart(updateCart());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCartItems(cart);
  }, [cart]);

  const changeQty = (id: string | number, count: number) => {
    const data = [...cartItems];
    for (const item of data) {
      if (item.id === id) {
        item.qty = count;
      }
    }
    setUserCart([...data]);
  };

  const deleteItem = (id: string | number) => {
    const data = [...cartItems];
    setUserCart(data.filter((item) => item.id !== id));
  };

  const removeGifts = () => {
    const _cart = [...cart];

    for (const i of _cart) {
      for (const g of i.gifts) {
        g.card = null;
        g.wrapping = null;
        (g as any).balloons = [];
      }
    }
    setGoCheckout(true);
    setUserCart(_cart);
    setOpenConfirmModal(false);
  };
  const isMount = useIsMount();

  useEffect(() => {
    if (!isMount) {
      if (goToCheckout) {
        action({ current: "gift", goCheckout: goToCheckout });
        removeProblems();
      } else {
        action({ current: "gift" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, goToCheckout]);

  return (
    <div className="w-full mt-10">
      <Modal
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        cancelable={true}
      >
        <div className="md:w-[480px] sm:w-screen xs:w-screen w-screen max-w-[480px] min-h-[300px] h-[max-content] bg-white rounded-lg p-4 flex flex-col justify-between">
          <div className=" w-full flex flex-wrap mb-5 items-center">
            <p className="flex-1 font-normal text-xl text-[#333] text-center">
              {t("client_bottomsheets_skipgiftwrap_title")}
            </p>
          </div>
          <div className=" flex flex-col items-center w-full">
            <WarningIcon />
            <p className=" text-[#999] font-normal text-sm text-center max-w-[340px] mt-4 leading-6">
              {t("client_bottomsheets_skipgiftwrap_message")}
            </p>
          </div>
          <div className="w-full mt-2">
            <div className="w-full flex flex-wrap justify-center">
              <Button
                className="flex-1 max-w-52 h-12 font-extrabold text-base rounded-full m-2"
                size="slim"
                theme="lightBlue"
                onClick={() => {
                  removeGifts();
                }}
              >
                {t("client_buttons_yesskip")}
              </Button>
              <Button
                className="flex-1 max-w-52 h-12 font-extrabold text-base rounded-full m-2"
                size="slim"
                onClick={() => {
                  setOpenConfirmModal(false);
                }}
              >
                {t("client_buttons_noletmepick")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <div className="w-full flex items-center  mb-10 ">
        <div className="relative h-10 sm:w-1 xs:w-[100px] w-[100px] ">
          <div className=" absolute -top-2">
            <Button
              size="slim"
              className="h-10 font-normal text-sm rounded-full m-2 px-0 w-[92px]"
              theme="lightBlue"
              onClick={() => {
                removeProblems();
                action({ next: "address", current: "gift" })
              }}
            >
              <PathIcon
                className={` ${locale?.includes("en") ? "rotate-180" : ""}  mx-2`}
              />
              <span className="mx-2 font-normal">{t("w-back")}</span>{" "}
            </Button>
          </div>
        </div>
        <p className="text-center lg:text-[32px] md:text-2xl  text-2xl font-extrabold text-[#333333] flex-1">
          {t("client_checkout_customizations")}
        </p>
      </div>
      {syncData?.allow_donation &&
        <div className="md:px-3 my-4">
          <WrappingDonationBanner />
        </div>
      }

      {wrappingProblems.find((p) => p.type === 'balloon-unavailable') ? (
        <p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 my-4">
          {t("client_cart_balloon_unavailable_itemremovehint")}
        </p>
      ) : wrappingProblems.length ? (
        <p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 my-4">
          {t("client_cart_itemremovehint")}
        </p>
      ) : null}

      {hasFeatureAnnouncement ? (
        <div className=" text-base font-normal w-full m-auto bg-[#FDE5D8] rounded-2xl p-4 mb-4 mt-10 flex items-center gap-2">
          <Image src="/imgs/new-feature-icon.svg" alt="new-feature-icon" width={96} height={96} />
          <div className="flex flex-col gap-2 ">
            <p className="text-[#38190D] font-extrabold md:text-2xl">{t("new_helium_balloons_title")}</p>
            <p className="text-[#956C4A]">{t("new_helium_balloons_description")}</p>
          </div>
        </div>
      ) : null}

      {cartItems.map((item) => (
        <CartItem
          step="gift"
          action={action}
          item={item}
          changeQty={(id: string | number, count: number) =>
            changeQty(id, count)
          }
          key={item.id}
          deleteItem={(id: string | number) => deleteItem(id)}
        />
      ))}
      <div className="w-full mt-5">
        <div className="w-full flex flex-wrap justify-center">
          <Button
            size="slim"
            className="h-12 font-extrabold text-base rounded-full m-2 w-56"
            theme="lightBlue"
            onClick={() => setOpenConfirmModal(true)}
          >
            {t("client_buttons_skip")}
          </Button>
          <Button
            size="slim"
            className=" h-12 font-extrabold text-base rounded-full m-2  w-56 disabled:bg-[#999] disabled:border-[#999]"
            isDisabled={ItemsQty ? false : true}
            onClick={() => {
              action({ current: "gift", goCheckout: true })
              removeProblems();
          }}
          >
            {t("client_buttons_next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Gift;
